/**
 * @module app.core
 */

import { autoinject } from 'aurelia-framework';
import { AssessmentContext } from 'app/core/lib/assessment-context';
import { QuestionResolver } from 'app/core/components/assessments/question-resolver';
import { IQuestionComponent, IQuestionConfig } from 'app/core/components/assessments/interfaces';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { modelIdMatcher } from 'app/shared/lib/matcher';
import { fromApiDecimal } from 'app/shared/models/essette-types';
import { QuestionBase } from 'app/core/components/assessments/questions/question-base';
import * as Core from 'app/core/models/essette-api';
import { guid } from 'app/shared/lib/guid';


@autoinject
export class QuestionRadioCustomElement extends QuestionBase implements IQuestionComponent {

    ////****************** Fields and Properties **************************************////
    protected selectedChoice: Core.AssessmentChoice | null = null;
    protected matcher = modelIdMatcher;
    protected id: guid = guid();
    protected ruleID: string = `question-rule-${this.id}`;

    ////****************** Lifecycle events (in order of execution) ******************////
    constructor(
        private readonly element: Element,
        protected readonly context: AssessmentContext,
        private readonly resolver: QuestionResolver,
        private readonly logger: AppLogger) {

        super(context);
        this.logger.source = this;

        if(!context || !context.currentSession) {
            throw new Error("No Assessment context is available");
        }

        if(!this.context.questionComponents.includes(this)) {
            this.context.questionComponents.push(this);
        }
    }

    protected async activate(config: IQuestionConfig): Promise<void> {
        this.question = config.question;
        this.required = !!this.question.flag?.isRequired;
        this.showHistory = config.showHistory;
        this.readonly = config.readonly;
        this.isValid = !this.required; //IQuestionConfig doesn't come with a default answer
        this.labelId = config.labelId;

        if(!this.resolver.isInitialized) { await this.resolver.init(); }
    }

    protected attached(): void {
        this.loadPreviousAnswer();
        this.updateScore();
    }


    ////****************** Events ***********************************************////
    protected choiceChanged(choice: Core.AssessmentChoice | null): void {
        this.isValid = !!choice?.id && !choice.followups?.find(q => {
            return !!q.flag?.isRequired; //again, no need to look for a default
        });

        if(this.context.attachment.isScored) {
            this.updateScore();
        }
    }


    ////****************** Functions ****************************************************////
    private async loadPreviousAnswer(): Promise<void> {
        const questionId = this.question && this.question.id;
        if(!questionId || !this.context.attachment.currentAnswers) { return; }

        const description = this.question && this.question.description || '';

        try {
            const answer = this.context.attachment.currentAnswers.find(a => {
                if(!a.question) { return false; }

                if(!this.question.sharedQuestion) {
                    return a.question.id === this.question.id;
                } else if(!!this.question.sharedQuestion && !!a.sharedQuestion) {
                    return this.question.sharedQuestion.id === a.sharedQuestion.id;
                }

                return false;
            });

            if(answer && answer.choice && this.question && this.question.choices) {
                this.selectedChoice = this.question.choices.find(c => {
                    if(!answer.sharedChoice && answer.choice) {
                        return answer.choice.id === c.id;
                    } else if(!!answer.sharedChoice && !!c.sharedChoice) {
                        return answer.sharedChoice.id === c.sharedChoice.id;
                    }

                    return false;
                }) || null;
            }
        }
        catch(error) {
            this.logger.error(`Unable to find existing answer(s) for question ${questionId} (${description})`);
            throw error;
        }
    }

    private updateScore(): void {
        if(!!this.selectedChoice && !!this.selectedChoice.score && !!this.element) {
            const score: number = fromApiDecimal(this.selectedChoice.score) || 0;
            const { question } = this;
            this.element.dispatchEvent(new CustomEvent('score-change', { bubbles: true, detail: { questionId: question.id, score: score } }));
        }
    }


    ////****************** IQuestionComponent ****************************************************////
    public saveAnswer = null;  // Not Implemented

    public getAnswers(): Core.AssessmentAnswer[] {
        if(this.selectedChoice) {
            const answer: Core.AssessmentAnswer = {
                question: { id: this.question && this.question.id || null },
                choice: { id: this.selectedChoice && this.selectedChoice.id || null }
            };

            return [answer];
        }

        return [];
    }

}
